import React, { FormEvent, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { api, errorMessage } from "./api";
import { SupportSession, VatExportJob, VatExportOptions, VatExportRequest } from "./types";

const quarters = [
  { value: 1, label: "Q1 · January–March" },
  { value: 2, label: "Q2 · April–June" },
  { value: 3, label: "Q3 · July–September" },
  { value: 4, label: "Q4 · October–December" },
];

function previousQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getUTCMonth() / 3);
  return { year: now.getUTCFullYear() - (quarter === 0 ? 1 : 0), quarter: quarter || 4 };
}

function uuid() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function mergeJobs(current: VatExportJob[], incoming: VatExportJob[]) {
  const jobs = new Map(current.map(job => [job.job_id, job]));
  incoming.forEach(job => {
    const previous = jobs.get(job.job_id);
    if (!previous || new Date(job.updated_at).getTime() >= new Date(previous.updated_at).getTime()) {
      jobs.set(job.job_id, job);
    }
  });
  return Array.from(jobs.values()).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 10);
}

const activeStatuses: VatExportJob["status"][] = ["queued", "running", "sending"];
const statusLabels: Record<VatExportJob["status"], string> = {
  queued: "Queued", running: "Preparing CSV", sending: "Sending email", sent: "Sent", failed: "Failed", unknown: "Delivery uncertain",
};

export default function VatExportPanel({ session }: { session: SupportSession }) {
  const manager = session.administrator.role === "manager";
  const [period] = useState(previousQuarter);
  const [options, setOptions] = useState<VatExportOptions | null>(null);
  const [productId, setProductId] = useState("");
  const [year, setYear] = useState(String(period.year));
  const [quarter, setQuarter] = useState(period.quarter);
  const [recipient, setRecipient] = useState("");
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [noticeStatus, setNoticeStatus] = useState<VatExportJob["status"]>("queued");
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [jobs, setJobs] = useState<VatExportJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState("");
  const [jobsReload, setJobsReload] = useState(0);
  const submittingRef = useRef(false);
  const requestRef = useRef<VatExportRequest | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!manager) return;
    let active = true;
    setLoading(true);
    setOptions(null);
    setOptionsError("");
    api.vatExportOptions().then(data => {
      if (!active) return;
      setOptions(data);
      setProductId(current => data.products.some(product => product.id === current) ? current : "");
      setRecipient(current => data.trusted_emails.includes(current) ? current : data.trusted_emails[0] || "");
    }).catch(requestError => {
      if (active) setOptionsError(errorMessage(requestError));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [manager, reload]);

  useEffect(() => {
    if (!manager) return;
    let active = true;
    setJobsLoading(true);
    setJobsError("");
    api.getVatExportJobs().then(data => {
      if (active) setJobs(current => mergeJobs(current, data.jobs));
    }).catch(requestError => {
      if (active) setJobsError(errorMessage(requestError));
    }).finally(() => { if (active) setJobsLoading(false); });
    return () => { active = false; };
  }, [manager, jobsReload]);

  useEffect(() => {
    if (!manager || jobsLoading || jobsError || !jobs.some(job => activeStatuses.includes(job.status))) return;
    let active = true;
    const timer = window.setTimeout(() => {
      api.getVatExportJobs().then(data => {
        if (active) setJobs(current => mergeJobs(current, data.jobs));
      }).catch(requestError => {
        if (active) setJobsError(errorMessage(requestError));
      });
    }, 3000);
    return () => { active = false; window.clearTimeout(timer); };
  }, [manager, jobs, jobsError, jobsLoading]);

  const numericYear = Number(year);
  const validSelection = Boolean(options?.products.some(product => product.id === productId)
    && options.trusted_emails.includes(recipient)
    && /^\d{4}$/.test(year) && numericYear >= 2000 && numericYear <= 9998
    && quarters.some(item => item.value === quarter));
  const disabled = loading || submitting;

  const clearStatus = () => { setError(""); setNotice(""); };
  const changeSelection = () => { clearStatus(); requestRef.current = null; setRetrying(false); };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!manager || !validSelection || loading || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    clearStatus();
    try {
      if (!requestRef.current) {
        requestRef.current = {
          product_id: productId, year: numericYear, quarter, recipient_email: recipient, idempotency_key: uuid(),
        };
      }
      const result = await api.emailVatExport(requestRef.current, session.csrf_token);
      if (!mountedRef.current) return;
      setJobs(current => mergeJobs(current, [result]));
      setJobsError("");
      setNotice(result.message);
      setNoticeStatus(result.status);
      requestRef.current = null;
      setRetrying(false);
    } catch (requestError) {
      if (mountedRef.current) { setError(errorMessage(requestError)); setRetrying(true); }
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  };

  if (!manager) return <Alert severity="info">Only managers can email VAT exports.</Alert>;

  return <Paper sx={{ p: { xs: 2, sm: 3 } }}>
    <Typography component="h2" variant="h5">Quarterly product VAT export</Typography>
    <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
      Export EU VAT for one product from paid, completed checkout sessions created in the selected UTC calendar quarter.
      The CSV includes all 27 EU countries, including those with zero VAT, and a total VAT amount. Different currencies have separate totals.
    </Typography>
    <Alert severity="info" sx={{ mb: 2.5 }}>
      The CSV is emailed only to a trusted recipient configured in Docker Compose. It is not downloaded in your browser.
    </Alert>
    {loading && <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} aria-label="Loading VAT export options" /></Box>}
    {optionsError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setReload(current => current + 1)}>Retry loading</Button>}>
      {optionsError}
    </Alert>}
    {options && options.products.length === 0 && <Alert severity="warning" sx={{ mb: 2 }}>No products are available for VAT export.</Alert>}
    {options && options.trusted_emails.length === 0 && <Alert severity="warning" sx={{ mb: 2 }}>No trusted VAT export emails are configured in Docker Compose. Configure recipients before exporting.</Alert>}
    <Stack component="form" onSubmit={submit} spacing={2} sx={{ maxWidth: 720 }}>
      <TextField id="vat-export-product" select label="Product" required fullWidth value={productId}
        disabled={disabled || !options?.products.length}
        onChange={event => { setProductId(event.target.value); changeSelection(); }}>
        {(options?.products || []).map(product => <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>)}
      </TextField>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField id="vat-export-year" label="Calendar year (UTC)" type="number" required fullWidth value={year}
          disabled={disabled} inputProps={{ min: 2000, max: 9998, step: 1 }}
          onChange={event => { setYear(event.target.value); changeSelection(); }} />
        <TextField id="vat-export-quarter" select label="Quarter (UTC)" required fullWidth value={quarter}
          disabled={disabled} onChange={event => { setQuarter(Number(event.target.value)); changeSelection(); }}>
          {quarters.map(item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
      </Stack>
      <TextField id="vat-export-recipient" select label="Trusted recipient" required fullWidth value={recipient}
        disabled={disabled || !options?.trusted_emails.length}
        onChange={event => { setRecipient(event.target.value); changeSelection(); }}>
        {(options?.trusted_emails || []).map(email => <MenuItem key={email} value={email}>{email}</MenuItem>)}
      </TextField>
      <Box><Button type="submit" variant="contained" disabled={disabled || !validSelection}>
        {submitting ? "Queueing export…" : retrying ? "Retry export request" : "Email VAT CSV"}
      </Button></Box>
      {submitting && <Typography role="status" color="text.secondary">Submitting the export request…</Typography>}
      {error && <Alert severity="error" aria-live="assertive">{error}</Alert>}
      {retrying && <Typography color="text.secondary">The request was not confirmed. Retrying uses the same request ID to avoid queueing it twice. You can also refresh recent export status below.</Typography>}
      {notice && <Alert severity={noticeStatus === "unknown" ? "warning" : noticeStatus === "failed" ? "error" : "success"} role="status">
        {notice} {activeStatuses.includes(noticeStatus) && "You can leave or close this page; the export continues in the background."}
      </Alert>}
    </Stack>
    <Divider sx={{ my: 3 }} />
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Typography component="h3" variant="h6">Recent export status</Typography>
      <Button onClick={() => setJobsReload(current => current + 1)} disabled={jobsLoading}>Refresh status</Button>
    </Stack>
    <Typography color="text.secondary" sx={{ mb: 2 }}>Your 10 most recent exports. Only delivery status is shown here; VAT figures and the CSV are sent by email only.</Typography>
    {jobsLoading && <CircularProgress size={24} aria-label="Loading export status" />}
    {jobsError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setJobsReload(current => current + 1)}>Retry status</Button>}>
      Could not refresh export status. {jobsError} Refreshing status will not queue or resend an export.
    </Alert>}
    {!jobsLoading && !jobsError && jobs.length === 0 && <Typography color="text.secondary">No recent exports.</Typography>}
    <Stack spacing={1.5} aria-label="Recent VAT export jobs">{jobs.map(job => <Box key={job.job_id} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Chip size="small" label={statusLabels[job.status]} color={job.status === "failed" ? "error" : job.status === "unknown" ? "warning" : job.status === "sent" ? "success" : "default"} sx={{ mb: 1 }} />
      <Typography>{job.message}</Typography>
      {job.status === "unknown" && <Typography color="warning.main" sx={{ mt: 0.5 }}>Check the trusted recipient's inbox before requesting another export. Delivery could not be confirmed.</Typography>}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Requested {new Date(job.created_at).toLocaleString()} · Updated {new Date(job.updated_at).toLocaleString()}
      </Typography>
    </Box>)}</Stack>
  </Paper>;
}
